// src/components/Stream/StreamHeader.jsx

import React, { useContext, useEffect, useRef, useState } from "react";
import { 
  IconButton, 
  Stack, 
  Typography, 
  Popover,
  Avatar,
  Box,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/HighlightOff";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ShieldIcon from '@mui/icons-material/Shield';
import BlockIcon from "@mui/icons-material/Block";
import Circle from "@mui/icons-material/Circle";
import MusicNote from "@mui/icons-material/MusicNote";
import CloseFullScreenIcon from "@mui/icons-material/FullscreenExit";
import OpenFullScreenIcon from "@mui/icons-material/Fullscreen";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cookieStorage from "../../../services/cookie-storage";
import notifier from "../../Notifier";

import emitter from "../../../EventEmitter";
import { SocketContext } from "../../../context/socket";
import { setStreamCount } from "../../../action/streamCount";
import {
  getAudienceId,
  getUserId,
  getUserDetail,
  putStreamRecord,
  formatJsonToSingleLine,
  kFormatter
} from "../../../constants/function";
import { Images } from "../../../assets/common";
import Search from "../../../pages/Search";
import ProfileBorder from "../../Header/ProfileBorder";
import { getBalance } from "../../../asyncAction/asyncBalance";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LivestreamPayPopup from "../../LivestreamPayPopup";
import ApiService from "../../../services/api-service";
import { useTheme } from "../../../context/ThemeContext";
import SocialProofLine from "../../SocialProofLine";
import useViewerFollows from "./useViewerFollows";

// Header dropdowns portal into the live page, not <body>: the page is a
// full-window overlay (and the fullscreen element), so anything portalled
// outside it is hidden underneath.
const liveContainer = () => document.getElementById("__streaming_container");
const POPOVER_Z = 20000; // above the live header (10000)

const popoverPaperSx = {
  mt: 1,
  width: 360,
  maxHeight: 460,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--surface, #0C0C12)',
  backgroundImage: 'none',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '14px',
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
  overflow: 'hidden',
};

const viewerActionSx = {
  width: 30,
  height: 30,
  borderRadius: '8px',
  color: 'rgba(255, 255, 255, 0.45)',
  '&:hover': { color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.06)' },
};

const followButtonSx = (following) => ({
  minWidth: 0,
  height: 28,
  px: 1.25,
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  color: following ? 'rgba(255, 255, 255, 0.7)' : '#0b0b0b',
  backgroundColor: following ? 'rgba(255, 255, 255, 0.06)' : 'var(--primary-color, #FFEE20)',
  '&:hover': {
    backgroundColor: following ? 'rgba(255, 255, 255, 0.1)' : 'var(--primary-color, #FFEE20)',
    opacity: following ? 1 : 0.9,
  },
  '&.Mui-disabled': { opacity: 0.5, color: following ? 'rgba(255, 255, 255, 0.7)' : '#0b0b0b' },
});

const ROLE_COLORS = {
  Host: '#FFEE20',
  Moderator: '#00C3FD',
  Viewer: 'var(--text-muted)',
};

/**
 * A component that displays:
 * - End stream button
 * - Fullscreen toggle
 * - Hide/Show stream
 * - The stream title
 * - Watchers count (via local watchers state)
 * - The streamCount from Redux
 * - Optional logic to handle “song-started” events, partial increments, etc.
 */
const __cohostInviteToastCache = new Map();
const __makeCoHostToastCache = new Map();

export default function StreamHeader({
  title,          // The title of the stream
  isOwner,        // Is the current user the host?
  isModerator = false,   // Is the current user one of the host's moderators?
  moderatorIds = [],     // Owner only: who is currently a moderator
  isJoined,       // Has the user joined the stream?
  playlistId,     // The channel or playlist ID
  streamData,     // The stream data object
  streamSong,     // Optional: "song-started" logic if needed
  obs,            // Optional: if OBS usage
  totalUsers,
  liveUser,       // Live users from parent component
  isFullScreen,   // Whether we are in fullscreen mode
  leaveStreamSentRef // Ref to track if leave-stream was already sent
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation("common");
  const socket = useContext(SocketContext).Socket;
  const { getCurrentBranding } = useTheme();
  const branding = getCurrentBranding();

  // Basic data for partial stream counting
  const token = cookieStorage.getCookie("Authorization");
  const userId = getUserId();

  // Redux states: track info, streamCount, etc.
  const { songs, index, track, pos } = useSelector((state) => state.streamSong);
  const { streamCount } = useSelector((state) => state.streamCount);
  // Ref mirror for the socket handler's monotonic clamp — reading
  // streamCount directly inside the handler would close over a stale
  // value across renders.
  const streamCountRef = useRef(streamCount);
  useEffect(() => { streamCountRef.current = streamCount; }, [streamCount]);
  const seek = useSelector((state) => state.seek);
  
  // Get user detail using the same function as main header
  const userDetail = getUserDetail();
  
  // Keep watchers in *local state* so updates re-render the UI
  const [watchers, setWatchers] = useState(liveUser || []);
  const [localWatchers, setLocalWatchers] = useState([]);
  const [watcherCount, setWatcherCount] = useState((liveUser || []).length || (isOwner && isJoined ? 1 : 0));
  
  // User list dropdown state
  const [userListAnchor, setUserListAnchor] = useState(null);
  const isUserListOpen = Boolean(userListAnchor);

  const { followState, isFollowPending, toggleFollow } = useViewerFollows(watchers, isUserListOpen);

  // Wallet dropdown state
  const [walletAnchor, setWalletAnchor] = useState(null);
  const isWalletOpen = Boolean(walletAnchor);

  // Buy tokens popup state
  const [openPayModel, setOpenPayModel] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  // Balance from Redux
  const { bal, hbar, matic, near } = useSelector((state) => state.balance);

  // PayPal link function
  const getPaypalLink = async (amount) => {
    if (amount >= 4.99 && amount <= 20000) {
      try {
        setPayLoading(true);
        const response = await ApiService.PAYPALLINK({
          amount: amount,
        });
        setPayLoading(false);
        // Open PayPal link in new window
        window.open(response["result"]["links"][1]["href"], '_blank');
        setOpenPayModel(false);
      } catch (error) {
        setPayLoading(false);
        notifier.error("Failed to get PayPal link");
      }
    }
  };

  // Local UI states
  const [isHidden, setIsHidden] = useState(false);
  const [songData, setSongData] = useState({});
  const [channelListenIncrement, setChannelListenIncrement] = useState(0);
  const [renderKey, setRenderKey] = useState(0);
  const [streamDuration, setStreamDuration] = useState('0:00');

  // Fetch balance on mount
  useEffect(() => {
    dispatch(getBalance());
  }, [dispatch]);

  // Stream duration timer - starts counting from when component mounts
  useEffect(() => {
    if (!streamData) return;

    console.log('[StreamHeader] Starting stream timer');
    const startTime = Date.now();

    const formatDuration = (seconds) => {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const updateTimer = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      const formatted = formatDuration(elapsed);
      setStreamDuration(formatted);
    };

    // Initial update
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => {
      console.log('[StreamHeader] Cleaning up timer');
      clearInterval(interval);
    };
  }, [streamData?._id]); // Only restart if stream ID changes


  useEffect(() => {
    if (!socket) {
      console.log("[StreamHeader] Socket not available");
      return;
    }

    console.log("[StreamHeader] Setting up socket listeners - socket connected:", socket.connected, "isOwner:", isOwner, "isJoined:", isJoined);

    // Just for reference if you have “livestream-streams” logic
    socket.on("livestream-streams", (data) => {
      if (data.channelName === playlistId) {
        setChannelListenIncrement(data.increment);
      }
    });

    // If host => new count => both owner and audience update (filter out viewer-count)
    socket.on("livestream-streamcount", (data) => {
      // Only process song-play-count, not viewer-count
      if (data.type === 'viewer-count') {
        return;
      }

      // Match against any id present on the payload (channelName, playlistId,
      // roomId). progressBroadcast sets both channelName and playlistId to
      // roomId but historic broadcasts may only set one - strict equality on
      // a single field caused web audience to silently drop every tick when
      // streamData/playlistId types diverged from the payload's field.
      const incomingIds = [
        data?.channelName?.toString?.() || data?.channelName,
        data?.playlistId?.toString?.() || data?.playlistId,
        data?.roomId?.toString?.() || data?.roomId,
      ].filter(Boolean);
      const myId = playlistId?.toString?.() || playlistId;

      if (myId && incomingIds.includes(myId)) {
        const incoming = Number(data.streamCount);
        if (!Number.isFinite(incoming)) return;
        // Monotonic clamp — never let an occasional regression
        // broadcast pull the audience's count backward.
        const current = Number(streamCountRef.current) || 0;
        if (incoming < current) return;
        dispatch(setStreamCount(incoming));
      }
    });

    // “song-started” => optional logic
    socket.on("song-started", (data) => {
      if (data.roomId === playlistId) {
        setSongData({ ...data });
      }
    });

    // watchers => store in watchers state
    socket.on("audience-in-stream", handleAudience);
    
    // Debug: log all socket events for this component
    console.log("[StreamHeader] Socket listeners set up. isOwner:", isOwner, "isJoined:", isJoined);

    // Handle raised hands notification (for hosts)
    socket.on("raised", (data) => {
      if (data.channelName === playlistId && isOwner) {
        if (data.raised) {
          notifier.info(`${data.user.firstName} ${data.user.lastName} raised their hand to become co-host`);
        }
      }
    });

    socket.on("makeCoHost", (data) => {
      if (data.channelName !== playlistId) return;
      const userName = data.user?.firstName && data.user?.lastName ? `${data.user.firstName} ${data.user.lastName}` : "User";
      const key = `${data.channelName}:${data.userId}:${data.user?.role}`;
      const now = Date.now();
      const last = __makeCoHostToastCache.get(key) || 0;
      if (now - last < 8000) return;
      __makeCoHostToastCache.set(key, now);

      if (isOwner) {
        if (data.user.role === "host" || data.user.role === "cohost") {
          notifier.success("Co-host invitation accepted");
          setWatchers((prev) => prev.map((w) => w?.user?._id === data.userId ? { ...w, role: "cohost", user: { ...w.user, role: "cohost" } } : w));
        } else {
          notifier.info(`${userName} is now audience`);
          setWatchers((prev) => prev.map((w) => w?.user?._id === data.userId ? { ...w, role: "audience", user: { ...w.user, role: "audience" } } : w));
        }
        return;
      }

      if (data.user.role === "host" || data.user.role === "cohost") {
        notifier.info(`${userName} is now a co-host`);
      } else {
        notifier.info(`${userName} is now audience`);
      }
    });

    socket.on("cohost-invite-response", (data) => {
      console.log('[cohost-invite-response]', data);
      if (data.channelName !== playlistId || !isOwner) return;
      const key = `${data.channelName}:${data.inviteId}:${data.status}`;
      const now = Date.now();
      const last = __cohostInviteToastCache.get(key) || 0;
      if (now - last < 8000) return;
      __cohostInviteToastCache.set(key, now);
      const s = data.status;
      if (s === "accepted") {
        notifier.success("Co-host invitation accepted");
        if (data.userId) {
          setWatchers((prev) => prev.map((w) => w?.user?._id === data.userId ? { ...w, role: "cohost", user: { ...w.user, role: "cohost" } } : w));
        }
      }
      if (s === "declined") notifier.info("Co-host invitation declined");
      if (s === "expired") notifier.info("Co-host invitation expired");
      if (s === "canceled") notifier.info("Co-host invitation canceled");
    });

    socket.on("cohost-assigned", (data) => {
      if (data.channelName !== playlistId || !data?.userId || !isOwner) return;
      const newRole = data.role === "host" || data.role === "cohost" ? "cohost" : "audience";
      setWatchers((prev) => prev.map((w) => w?.user?._id === data.userId ? { ...w, role: newRole, user: { ...w.user, role: newRole } } : w));
    });

    socket.on("cohost-role-changed", (data) => {
      if (data.channelName !== playlistId || !data?.userId || !isOwner) return;
      const newRole = data.role === "host" || data.role === "cohost" ? "cohost" : "audience";
      setWatchers((prev) => prev.map((w) => w?.user?._id === data.userId ? { ...w, role: newRole, user: { ...w.user, role: newRole } } : w));
    });

    return () => {
      socket.off("livestream-streams");
      socket.off("livestream-streamcount");
      socket.off("song-started");
      socket.off("audience-in-stream", handleAudience);
      socket.off("raised");
      socket.off("makeCoHost");
      socket.off("cohost-invite-response");
      socket.off("cohost-role-changed");
      socket.off("cohost-assigned");
    };
  }, [socket, streamSong, playlistId, isOwner]);



  // Update watchers when liveUser prop changes - this takes priority over socket events
  useEffect(() => {
    console.log("[StreamHeader] liveUser effect triggered - liveUser:", liveUser?.length || 0, "isOwner:", isOwner, "isJoined:", isJoined, "currentCount:", watcherCount);

    if (liveUser && Array.isArray(liveUser) && liveUser.length > 0) {
      console.log("[StreamHeader] => liveUser prop updated => count:", liveUser.length, "users:", liveUser.map(u => u.user?.firstName || 'Unknown'));
      // Create a new array reference to trigger React re-render
      setWatchers([...liveUser]);
      setWatcherCount(liveUser.length);
    } else {
      // The parent has no roster yet. This header also listens for
      // audience-in-stream itself, so it may already hold a good list; the
      // effect below keeps the count in step with it. Clearing here threw that
      // list away, and because `isJoined` is a dependency it ran again the
      // moment the user joined, which is why the viewer list opened empty.
      console.log("[StreamHeader] No liveUser prop yet; keeping any roster this header received");
    }
  }, [liveUser, isOwner, isJoined]);


  // 1) Fullscreen toggling
  const toggleFullScreen = () => {
    if (isFullScreen) {
      emitter.emit("closeFullScreen");
    } else {
      emitter.emit("openFullScreen");
    }
  };

  // 2) Hide/Show stream
  const hideStream = () => {
    emitter.emit("closeNft");
    const doc = document.getElementById("__streaming_container");
    const streamTippers = document.getElementById("_streamTippers");
    const streaming_control = document.getElementById("_streaming_control");
    const streaming_canvas = document.getElementById("_streaming_canvas");
    if (doc) doc.style.display = "none";
    if (streamTippers) streamTippers.style.display = "none";
    if (streaming_control) streaming_control.style.display = "none";
    if (streaming_canvas) streaming_canvas.style.display = "none";
    setIsHidden(true);
  };

  const showStream = () => {
    emitter.emit("closeNft");
    const doc = document.getElementById("__streaming_container");
    const streamTippers = document.getElementById("_streamTippers");
    const streaming_control = document.getElementById("_streaming_control");
    const streaming_canvas = document.getElementById("_streaming_canvas");
    if (doc) doc.style.display = "block";
    if (streamTippers) streamTippers.style.display = "block";
    if (streaming_control) streaming_control.style.display = "flex";
    if (streaming_canvas) streaming_canvas.style.display = "flex";
    setIsHidden(false);
  };

  // 4) If host => broadcast new streamCount on changes
  useEffect(() => {
    if (socket && isJoined && isOwner) {
      socket.emit("livestream-streamcount", {
        JWT: token,
        streamCount,
        channelName: playlistId,
      });
    }
  }, [streamCount]);

  // 5) Hide/Show stream events
  useEffect(() => {
    emitter.on("hideStream", hideStream);
    emitter.on("showStream", showStream);
    return () => {
      emitter.removeListener("hideStream", hideStream);
      emitter.removeListener("showStream", showStream);
    };
  }, []);
  // Update watcherCount when watchers change
useEffect(() => {
  if (Array.isArray(watchers)) {
    setWatcherCount(watchers.length);
  }
}, [watchers]);

  // 6) If host + joined => hide share screen in /LiveVideoStreaming
  useEffect(() => {
    if (isOwner && isJoined && history.location.pathname.includes("/LiveVideoStreaming")) {
      const shareScreenBtn = document.getElementById("_share_screen_btn");
      if (shareScreenBtn) shareScreenBtn.style.display = "none";
    }
  }, [isJoined]);



  const handleAudience = (data) => {
    console.log("[StreamHeader] handleAudience received:", {
      channelName: data.channelName,
      playlistId: playlistId,
      streamDataId: streamData?._id,
      liveUsersCount: data.liveUsers?.length,
      match: data.channelName === playlistId || data.channelName === streamData?._id,
      isOwner: isOwner,
      isJoined: isJoined,
      currentWatcherCount: watcherCount
    });

    // Match against any id on the payload (channelName/playlistId/roomId)
    // and any id we know for our stream. Strict equality on a single field
    // caused web audience to silently drop audience-in-stream broadcasts
    // when backend keyed by playlistId but the client only had channelName
    // (or vice-versa due to ObjectId/string shape mismatches).
    const incomingIds = [
      data?.channelName?.toString?.() || data?.channelName,
      data?.playlistId?.toString?.() || data?.playlistId,
      data?.roomId?.toString?.() || data?.roomId,
    ].filter(Boolean);
    const ourIds = [playlistId, streamData?._id?.toString?.() || streamData?._id].filter(Boolean).map(String);
    const matched = incomingIds.some((id) => ourIds.includes(id));
    if (matched) {
      // Always update from socket events regardless of owner status
      if (data.liveUsers && Array.isArray(data.liveUsers)) {
        console.log("[StreamHeader] Updating watchers from", watchers.length, "to", data.liveUsers.length);
        // Create a new array reference to trigger React re-render
        setWatchers([...data.liveUsers]);
        setWatcherCount(data.liveUsers.length);
      } else {
        // If no users data but we're in a stream, show minimum count
        const minCount = isJoined ? 1 : 0;
        console.log("[StreamHeader] No users data, setting minimum count:", minCount);
        setWatcherCount(minCount);
        setWatchers([]);
      }
  
        // Optionally record “stop/start”
        const playingIndex = localStorage.getItem("streamIndex");
        if (songs.length > 0 && playingIndex && playingIndex >= 0) {
          const currentTimeInSong = parseFloat(localStorage.getItem("curretTimeInSong") || 0);
  
          // "stop" for old watchers
          putRecord(
            "stop",
            songs[playingIndex]._id,
            playlistId,
            playingIndex,
            currentTimeInSong,
            new Date().getTime(),
            songs,
            watchers
          );
  
          // "start" for new watchers
          putRecord(
            "start",
            songs[playingIndex]._id,
            playlistId,
            playingIndex,
            currentTimeInSong,
            new Date().getTime(),
            songs,
            data.liveUsers
          );
        }
    }
  };


    useEffect(() => {
      setLocalWatchers(watchers);
    }, [watchers]);



  // If you have a “putRecord” logic for Kinesis or logs
  const putRecord = async (
    event,
    musicId,
    playlistId,
    index,
    timeInSong,
    associationId,
    playlistSongs,
    liveUserData
  ) => {
    const watchersArr = await getAudienceId(liveUserData);
    const payload = {
      eventType: event,
      timestamp: new Date().getTime(),
      userId: getUserId(),
      musicId,
      playlistId,
      livestreamId: playlistId,
      songDetails: playlistSongs[index],
      timeInSong,
      associationId,
      audienceArray: watchersArr,
    };
    putStreamRecord(payload);
  };

  // Handle user list dropdown
  const viewerChipRef = React.useRef(null);
  const handleUserListClick = () => {
    if (viewerChipRef.current) {
      setUserListAnchor(viewerChipRef.current);
    }
  };

  const handleUserListClose = () => {
    setUserListAnchor(null);
  };

  // Get user role for display
  const getUserRole = (watcher, isOwner) => {
    // Check if watcher has user object with role
    if (watcher.user && watcher.user.role === "host") return "Host";
    // Check if watcher itself has role (for backward compatibility)
    if (watcher.role === "host") return "Host";
    // Check if this is the owner viewing their own stream
    if (isOwner && watcher.user && watcher.user._id === getUserId()) return "Host";
    if (isOwner && watcher._id === getUserId()) return "Host";
    return "Viewer";
  };

  // Handle invite user to co-host
  const handleInviteUser = (user) => {
    if (socket && isOwner) {
      const newRole = user.role === "audience" ? "host" : "audience";
      
      const inviteData = {
        JWT: token,
        channelName: playlistId,
        userId: user._id,
        role: newRole,
        mic: true,  // Enable mic by default for co-hosts
        camera: true,  // Enable camera by default for co-hosts
        mutedBy: null,
        changedToHost: newRole === "host",
        inviteOnly: newRole === "host"
      };
      
      if (newRole === "host") {
        console.log('[cohost-invite:create] emitting', { channelName: playlistId, userId: user._id });
        socket.emit("cohost-invite:create", { JWT: token, channelName: playlistId, userId: user._id });
      } else {
        if (socket.timeout) {
          socket.timeout(5000).emit("cohost-remove", { JWT: token, channelName: playlistId, userId: user._id }, (err, res) => {
            if (err) {
              notifier.error("Failed to remove co-host");
              return;
            }
            const payload = Array.isArray(res) && res.length ? res[res.length - 1] : res;
            if (payload && payload.status === 'ok') {
              notifier.info("Co-host removed");
            } else if (payload && payload.status === 'is_owner') {
              notifier.error("Cannot remove the primary host");
            } else if (payload && payload.status === 'not_owner') {
              notifier.error("Only the host can remove a co-host");
            } else if (payload && payload.status === 'not_in_stream') {
              notifier.error("User is not in the stream");
            } else if (payload && payload.status === 'already_audience') {
              notifier.info("User is already an audience");
            } else if (payload && payload.status === 'not_removable') {
              notifier.error("Only co-hosts can be removed");
            } else {
              notifier.error("Failed to remove co-host");
            }
          });
        } else {
          socket.emit("cohost-remove", { JWT: token, channelName: playlistId, userId: user._id });
        }
      }
      
      // Close the dropdown after action
      handleUserListClose();
      
      notifier.info(`${newRole === "host" ? "Invite sent to" : "Demoting"} ${user.firstName || "User"}`);
    }
  };

  // Handle kick user
  // The socket has no "kick-user" handler; the API does, and it lets the host
  // and moderators through (a moderator cannot remove the host or a moderator).
  const handleKickUser = async (user) => {
    if (!user?._id || !playlistId) return;
    try {
      await ApiService.LIVESTREAM_KICK(playlistId, user._id);
    } catch (e) {
      console.warn('[StreamHeader] kick failed:', e?.response?.data?.message || e?.message);
    }
  };

  const handleToggleModerator = async (user) => {
    if (!user?._id) return;
    const has = moderatorIds.includes(String(user._id));
    try {
      if (has) await ApiService.LIVESTREAM_MODERATOR_REMOVE(user._id, playlistId);
      else await ApiService.LIVESTREAM_MODERATOR_ADD(user._id, playlistId);
    } catch (e) {
      console.warn('[StreamHeader] moderator change failed:', e?.response?.data?.message || e?.message);
    }
  };

  // Handle tip user
  const handleTipUser = (user) => {
    // Emit event to open tip modal for specific user
    if (user._id !== getUserId()) {
      emitter.emit("openTipModal", { user });
    }
  };

  return (
    <Box
      key={renderKey}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px",
        position: "relative"
      }}
    >
      {/* LEFT side => SIKI Logo + buttons */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <img
          src={branding.logo}
          alt={branding.logoAlt || "Logo"}
          onClick={async (e) => {
            try {
              if (leaveStreamSentRef) {
                leaveStreamSentRef.current = true;
              }
              if (isJoined) {
                emitter.emit("forcefulLeaveStream");
                await new Promise(resolve => setTimeout(resolve, 250));
              }
              history.push("/home");
            } catch (error) {
              console.error("Error in SIKI logo click:", error);
              history.push("/home");
            }
          }}
          style={{
            cursor: "pointer",
            height: "36px",
            display: "block",
            alignSelf: "center"
          }}
        />

        <Box sx={{ height: "24px", width: "1px", background: "rgba(255,255,255,0.08)" }} />

        {isOwner && (
          <Button
            id="closeIcon"
            title="End stream"
            onClick={async () => {
              try {
                emitter.emit("forcefulLeaveStream");
                await new Promise(resolve => setTimeout(resolve, 250));
                emitter.emit("endStream");
              } catch (error) {
                console.error("Error ending stream:", error);
                emitter.emit("endStream");
              }
            }}
            sx={{
              px: 2, py: 0.5, minWidth: "auto", height: 34,
              borderRadius: "8px",
              background: "rgba(255, 59, 92, 0.15)",
              border: "1px solid rgba(255, 59, 92, 0.3)",
              color: "#FF3B5C",
              fontFamily: "var(--font-body)",
              fontSize: "11px", fontWeight: 600,
              letterSpacing: "0.5px", textTransform: "none",
              position: "relative", overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(255, 59, 92, 0.25)",
                borderColor: "rgba(255, 59, 92, 0.5)",
                boxShadow: "0 0 20px rgba(255, 59, 92, 0.2)",
              },
              "&::before": {
                content: '""', position: "absolute", top: 0, left: "-100%",
                width: "100%", height: "100%",
                background: "linear-gradient(90deg, transparent, rgba(255,59,92,0.1), transparent)",
                animation: "endBtnShimmer 3s infinite",
              },
              "@keyframes endBtnShimmer": {
                "0%": { left: "-100%" },
                "100%": { left: "100%" },
              },
            }}
          >
            End Live
          </Button>
        )}

        {!isOwner && isJoined && (
          <IconButton
            id="leaveIcon"
            title="Leave stream"
            onClick={async () => {
              try {
                if (leaveStreamSentRef) leaveStreamSentRef.current = true;
                emitter.emit("forcefulLeaveStream");
                await new Promise(resolve => setTimeout(resolve, 250));
                history.push("/home");
              } catch (error) {
                console.error("Error leaving stream:", error);
                history.push("/home");
              }
            }}
            sx={{
              width: 34, height: 34, borderRadius: "8px",
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
              "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)" },
            }}
          >
            <CloseIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} />
          </IconButton>
        )}

        <IconButton
          onClick={toggleFullScreen}
          sx={{
            width: 34, height: 34, borderRadius: "8px",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
            "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)" },
          }}
        >
          {isFullScreen ? (
            <CloseFullScreenIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }} />
          ) : (
            <OpenFullScreenIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }} />
          )}
        </IconButton>
      </Stack>

      {/* CENTER => Live indicator + Title + Stat chips */}
      <Stack direction="row" alignItems="center" spacing={3} sx={{ flex: 1, justifyContent: "center", position: "relative", zIndex: 2 }}>
        {/* Live indicator with ping */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ position: "relative", width: 10, height: 10 }}>
            <Box sx={{
              width: 10, height: 10, borderRadius: "50%", backgroundColor: "#FF3B5C",
              position: "relative", zIndex: 2,
            }} />
            <Box sx={{
              position: "absolute", inset: "-4px", borderRadius: "50%",
              border: "1.5px solid #FF3B5C",
              animation: "ping 2s ease-out infinite",
              "@keyframes ping": {
                "0%": { transform: "scale(1)", opacity: 0.8 },
                "100%": { transform: "scale(2)", opacity: 0 },
              },
            }} />
          </Box>
          <Typography sx={{
            fontFamily: "var(--font-display)", fontSize: "10px", fontWeight: 800,
            color: "#FF3B5C", letterSpacing: "2px", textTransform: "uppercase",
          }}>
            Live
          </Typography>
        </Stack>

        {/* Room name + social proof (hidden below md so the bar never overflows) */}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "var(--font-display)", fontSize: "14px", fontWeight: 700,
            color: "#fff", letterSpacing: "0.5px",
          }}>
            {streamData?.name || streamData?.title || 'Live Stream'}
          </Typography>
          <SocialProofLine type="livestream" id={streamData?._id} sx={{ fontSize: 11, display: { xs: "none", md: "block" }, whiteSpace: "nowrap" }} />
        </Box>

        {/* Stat chips */}
        <Stack direction="row" spacing={1}>
          {/* Viewers chip */}
          <Stack
            ref={viewerChipRef}
            direction="row" alignItems="center" spacing={0.8}
            onClick={(e) => {
              e.stopPropagation();
              handleUserListClick();
            }}
            sx={{
              cursor: "pointer", padding: "5px 12px", borderRadius: "8px",
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
              "&:hover": { background: "rgba(255,255,255,0.08)" },
              userSelect: "none",
            }}
          >
            <PersonIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 13 }} />
            <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "#fff", fontWeight: 700 }}>
              {watcherCount || 0}
            </Typography>
          </Stack>

          {/* Moderator chip: the only place the badge appears, so a moderator
              can tell they hold the role without opening the viewer list. */}
          {isModerator && !isOwner && (
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{
              padding: "5px 12px", borderRadius: "8px",
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,238,32,0.25)",
            }}>
              <ShieldIcon sx={{ color: "#FFEE20", fontSize: 13 }} />
              <Typography sx={{
                fontFamily: "var(--font-mono)", fontSize: "12px",
                color: "#FFEE20", fontWeight: 700, letterSpacing: "1px",
              }}>
                MOD
              </Typography>
            </Stack>
          )}

          {/* Earnings chip */}
          <Stack direction="row" alignItems="center" spacing={0.8} sx={{
            padding: "5px 12px", borderRadius: "8px",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
          }}>
            <MusicNote sx={{ color: "rgba(255,255,255,0.35)", fontSize: 13 }} />
            <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "#FFEE20", fontWeight: 700 }}>
              {streamCount.toFixed(2)}
            </Typography>
          </Stack>

          {/* Timer chip */}
          {isOwner && (
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{
              padding: "5px 12px", borderRadius: "8px",
              background: "rgba(255,59,92,0.12)", border: "1px solid rgba(255,59,92,0.15)",
            }}>
              <AccessTimeIcon sx={{ color: "rgba(255,255,255,0.35)", fontSize: 13 }} />
              <Typography sx={{
                fontFamily: "var(--font-mono)", fontSize: "12px",
                color: "#FF3B5C", fontWeight: 700, fontVariantNumeric: "tabular-nums",
              }}>
                {streamDuration}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* RIGHT side => Search + Wallet + Profile */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box id="searchBox" sx={{ minWidth: "150px", maxWidth: "250px" }}>
          <Search />
        </Box>

        <IconButton
          title="Wallet"
          onClick={(e) => {
            // Re-fetch the balance every time the wallet opens. The mount-time
            // getBalance() can fail silently (e.g. a backend/DB blip) and leave
            // Redux stuck at 0; fetching on open guarantees a fresh balance
            // rather than a stale/blank one.
            dispatch(getBalance());
            setWalletAnchor(e.currentTarget);
          }}
          sx={{
            width: 34, height: 34, borderRadius: "8px",
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
            "&:hover": { background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)" },
          }}
        >
          <AccountBalanceWalletIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }} />
        </IconButton>

        <Box
          id="userProfileIcon"
          onClick={() => { window.open("/app/Profile", "_blank"); }}
          sx={{ cursor: "pointer", position: "relative", width: "48px", height: "48px" }}
        >
          <ProfileBorder progress={userDetail["percentage"]} />
          <img
            style={{
              zIndex: 1, objectFit: "cover", position: "absolute",
              width: "34px", height: "30px", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 99%, 25% 99%, 0% 50%)",
              backgroundColor: "#1e1e1e", pointerEvents: "none",
            }}
            src={
              userDetail["profileImg"] && userDetail["profileImg"]["filePath"] != ""
                ? userDetail["profileImg"]["filePath"]
                : ""
            }
            alt="user profile"
          />
        </Box>
      </Stack>

    <Popover
      open={isUserListOpen}
      anchorEl={userListAnchor}
      onClose={handleUserListClose}
      container={liveContainer}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: POPOVER_Z }}
      PaperProps={{ sx: { ...popoverPaperSx, width: 400, maxWidth: 'calc(100vw - 32px)' } }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        px: 2, py: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
      }}>
        <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
          Viewers ({watcherCount || 0})
        </Typography>
      </Box>

      <Box key={watcherCount} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5 }}>
        {watchers.map((watcher) => {
          const viewer = watcher.user || {};
          const isSelf = viewer._id === getUserId();
          const isMod = moderatorIds.includes(String(viewer._id));
          const baseRole = getUserRole(watcher, isOwner);
          const role = baseRole === 'Host' ? 'Host' : isMod ? 'Moderator' : 'Viewer';
          const name = `${viewer.firstName || 'Unknown'} ${viewer.lastName || ''}`.trim();

          return (
            <Box
              key={watcher._id || watcher.uid}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
              }}
            >
              <Avatar src={viewer.profileImg?.filePath} sx={{ width: 34, height: 34, fontSize: 14 }}>
                {viewer.firstName?.charAt(0) || 'U'}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ color: '#fff', fontSize: '13px', fontWeight: 600 }}>
                  {name}{isSelf ? ' (you)' : ''}
                </Typography>
                <Typography noWrap sx={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  <Box component="span" sx={{ color: ROLE_COLORS[role] }}>{role}</Box>
                  {' · '}
                  {watcher.platform === 'mobile' ? 'Mobile' : 'Web'}
                </Typography>
              </Box>

              {!isSelf && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {followState[String(viewer._id)] !== undefined && (
                    <Button
                      size="small"
                      disabled={isFollowPending(viewer._id)}
                      sx={followButtonSx(followState[String(viewer._id)])}
                      onClick={(e) => { e.stopPropagation(); toggleFollow(viewer._id); }}
                    >
                      {followState[String(viewer._id)]
                        ? t("follow.followList.button2")
                        : t("follow.followList.button1")}
                    </Button>
                  )}

                  <IconButton size="small" title="Tip" sx={viewerActionSx}
                    onClick={(e) => { e.stopPropagation(); handleTipUser(viewer); }}>
                    <img src={Images.tip_player} alt="" style={{ width: 15, height: 15 }} />
                  </IconButton>

                  {isOwner && (
                    <IconButton size="small" sx={{ ...viewerActionSx, ...(isMod && { color: '#FFEE20' }) }}
                      title={isMod ? 'Remove moderator' : 'Make moderator'}
                      onClick={(e) => { e.stopPropagation(); handleToggleModerator(viewer); }}>
                      <ShieldIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}

                  {isOwner && (
                    <IconButton size="small" sx={viewerActionSx}
                      title={viewer.role === 'audience' ? 'Make co-host' : 'Remove co-host'}
                      onClick={(e) => { e.stopPropagation(); handleInviteUser(viewer); }}>
                      {viewer.role === 'audience' ? <PersonAddIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  )}

                  {/* Moderators may only remove audience */}
                  {(isOwner || (isModerator && viewer.role === 'audience')) && (
                    <IconButton size="small" title="Remove from stream"
                      sx={{ ...viewerActionSx, '&:hover': { color: '#FF3B5C', backgroundColor: 'rgba(255, 59, 92, 0.1)' } }}
                      onClick={(e) => { e.stopPropagation(); handleKickUser(viewer); }}>
                      <BlockIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
          );
        })}

        {watchers.length === 0 && (
          <Typography sx={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', py: 4 }}>
            No viewers yet
          </Typography>
        )}
      </Box>
    </Popover>

    {/* Wallet Dropdown */}
    <Popover
      open={isWalletOpen}
      anchorEl={walletAnchor}
      onClose={() => setWalletAnchor(null)}
      container={liveContainer}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          width: 320,
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
      sx={{ zIndex: POPOVER_Z }}
    >
      <Box sx={{ p: 2 }}>
        {/* Total Balance */}
        <Typography
          variant="h4"
          sx={{
            color: '#FFD700',
            textAlign: 'center',
            fontWeight: 'bold',
            mb: 0.5
          }}
        >
          ${(((bal || 0) * 0.05) + (hbar || 0) + (matic || 0) + (near || 0)).toFixed(2)}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            textAlign: 'center',
            fontSize: '12px',
            mb: 2
          }}
        >
          -- / -- XP
        </Typography>

        {/* Token List */}
        <Stack spacing={1}>
          {/* SIKI */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            p: 1.5
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <img src={Images.tip_player} alt="SIKI" style={{ width: 32, height: 32 }} />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>SIKI</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                  {kFormatter((bal || 0).toFixed(2))} SIKI
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                ${((bal || 0) * 0.05).toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Hedera */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            p: 1.5
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <img src={Images.hbar} alt="Hedera" style={{ width: 32, height: 32 }} />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>Hedera</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                  {kFormatter((hbar || 0).toFixed(2))} HBAR
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                ${(hbar || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Near */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            p: 1.5
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <img src={Images.near_color} alt="Near" style={{ width: 32, height: 32 }} />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>Near</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                  {kFormatter((near || 0).toFixed(2))} NEAR
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                ${(near || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* MATIC */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            p: 1.5
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <img src={Images.polygon_color} alt="MATIC" style={{ width: 32, height: 32 }} />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 500 }}>MATIC</Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                  {kFormatter((matic || 0).toFixed(2))} MATIC
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                ${(matic || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Divider */}
        <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }} />

        {/* Buy Tokens Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            setWalletAnchor(null);
            setOpenPayModel(true);
          }}
          sx={{
            backgroundColor: '#4a4a4a',
            color: '#fff',
            textTransform: 'none',
            borderRadius: '8px',
            py: 1.5,
            '&:hover': {
              backgroundColor: '#5a5a5a'
            }
          }}
        >
          💰 Buy Tokens
        </Button>
      </Box>
    </Popover>

    {/* Buy Tokens PayPal Popup */}
    <LivestreamPayPopup
      open={openPayModel}
      handleClose={() => setOpenPayModel(false)}
      handleSubmit={(amount, type) => {
        getPaypalLink(amount);
      }}
      loading={payLoading}
      onlyPayPal={true}
    />
    </Box>

  );
}
